import { AfterViewInit, Directive, ElementRef, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { IPageDataV3 } from '@df/catalog/cms/cms-model.interface';
import { App } from '@df/core/app';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { UiComponent } from '@df/ui/ui.component';
import { NICmsContentComponent } from '@icms/core/content';
import { ICmsDataChangeService, NICmsDataChange } from '@icms/core/data/change/icms-data-change.service';
import { ICmsPageDecorator } from '../../../cms/page/icms-page.decorator';
import { PageHeaderService } from '../../../layout/page/header/page-header.service';
import { IPageConfig } from '../../config/page.interface';

declare module '@df/catalog/cms/cms-model.interface' {
    export interface IPageDataV2 {
        components: NICmsContentComponent.IData[];
    }
}

@Directive()
export class PageViewAbstract<T extends IPageConfig = IPageConfig> extends UiComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input({
        required: true
    })
    model!: ICmsPageDecorator<IPageDataV3<T>>;

    protected iCmsDataChangeService = inject(ICmsDataChangeService);
    protected pageHeaderService = inject(PageHeaderService);
    private app = inject(App);
    private elementRef = inject(ElementRef);
    private afterContentInit = inject(AfterContentInitDirectiveService);

    override ngOnInit(): void {
        this.subscriptions.add(
            this.iCmsDataChangeService.afterChange$.subscribe(event => {
                if (event.model === this.model && event.changeType === NICmsDataChange.EChangeType.change) {
                    this.updateClassNames();

                    this.detectChanges();
                }
            })
        );

        this.updateClassNames();

        this.subscriptions.add(this.client.obs.breakpoint.subscribe(() => this.updateSize()));
        this.updateSize();

        if (this.app.isPlatformBrowser && window !== undefined) {
            window.screen.orientation.onchange = () => this.updateSize();
        }

        super.ngOnInit();
    }

    ngAfterViewInit(): void {
        this.timeoutService.setTimeout(() => this.afterContentInit.unlock(this), 1000);
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        // reset page header settings
        this.timeoutService.platformSafe(() => this.pageHeaderService.resetHighPriorityState()); // injector may be destroyed
    }

    protected updateClassNames(): void {
        this.pageHeaderService.setHighPriorityState({
            transparent: this.transparent,
            lightMode: this.lightMode,
            hideUp: this.hideUp
        });
    }

    protected updateSize(): void {
        this.elementRef.nativeElement.style.setProperty('--client-height', `${this.client.height}px`);
    }

    /**
     * Fallback to old saved data
     */
    protected get transparent(): boolean {
        return this.model.data?.typeData?.headerTransparent || (<any>this.model.data?.typeData)?.header_transparent;
    }

    protected get lightMode(): boolean {
        return this.model.data?.typeData?.headerLightMode || (<any>this.model.data?.typeData)?.header_light_mode;
    }

    protected get hideUp(): boolean {
        return this.model.data?.typeData?.headerHideUp || (<any>this.model.data?.typeData)?.header_hide_up;
    }
}
